/**
 * A manager class for handling socket connection IDs
 */
export class SocketManager {
  private static instance: SocketManager;
  // Maps connection ID (IP:port) to socket key
  private connections: Map<string, any> = new Map();

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  /**
   * Get the singleton instance of SocketManager
   */
  public static getInstance(): SocketManager {
    if (!SocketManager.instance) {
      SocketManager.instance = new SocketManager();
    }
    return SocketManager.instance;
  }

  /**
   * Register a socket connection
   * @param ip IP address of the connection
   * @param socket The socket instance to attach close handler
   */
  public registerConnection(ip: string, socket: ISocket): void {
    const connectionId = `${ip}`;
    if (this.connections.get(connectionId) === socket) return; // already registered, skip re-wrapping onClose
    this.connections.set(connectionId, socket);
    if(!socket?.socketKey) throw new Error('Socket key is already registered');
    if (socket) {
      const originalOnClose = socket.onClose;
      socket.onClose = (hasError: any) => {
        console.log(`Socket ${socket.socketKey} closed, hasError: ${hasError}`);
        this.removeConnectionBySocketKey(socket.socketKey);
        if (typeof originalOnClose === 'function') {
          originalOnClose(hasError);
        }
      };
    }
  }

  /**
   * Get a socket key by its IP and port
   * @param ip IP address of the connection
   * @returns The socket key if found, undefined otherwise
   */
  public getSocket(ip: string): ISocket | undefined {
    const connectionId = `${ip}`;
    return this.connections.get(connectionId);
  }

  /**
   * Find connection ID by socket key
   * @param socketKey The socket key
   * @returns The connection ID if found, undefined otherwise
   */
  public getConnectionId(socketKey: string): string | undefined {
    for (const [connectionId, socket] of this.connections.entries()) {
      if (socket.socketKey === socketKey) {
        return connectionId;
      }
    }
    return undefined;
  }


  /**
   * Remove a connection by its socket key
   * @param socketKey The socket key
   * @returns true if the connection was removed, false otherwise
   */
  public removeConnectionBySocketKey(socketKey: string): boolean {
    const connectionId = this.getConnectionId(socketKey);
    if (connectionId) {
      return this.connections.delete(connectionId);
    }
    return false;
  }

  /**
   * Clear all connections
   */
  public clearConnections(): void {
    this.connections.clear();
  }

}

/**
 * Interface representing a Socket instance
 */
export interface ISocket {
  // Properties
  socketKey: string;
  _state: number;
  state: number; // Getter for _state

  // Event handlers
  onData: ((data: Uint8Array) => void) | null;
  onClose: ((hasError: boolean) => void) | null;
  onError: ((errorMessage: string) => void) | null;

  // Methods
  open: (host: string, port: number,
         success?: () => void,
         error?: (errorMessage: string) => void) => void;
  write: (data: Uint8Array | number[],
         success?: () => void,
         error?: (errorMessage: string) => void) => void;
  shutdownWrite: (success?: () => void,
                 error?: (errorMessage: string) => void) => void;
  close: (success?: () => void,
         error?: (errorMessage: string) => void) => void;
  _ensureState: (requiredState: number,
                errorCallback: (message: string) => void) => boolean;
}

/**
 * Socket state enum
 */
export enum SocketState {
  CLOSED = 0,
  OPENING = 1,
  OPENED = 2,
  CLOSING = 3
}
